import React, { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api, {
    AiFeesLeaderboardEntry,
    AiFeesLeaderboardResponse,
    MintersLeaderboardEntry,
    MintersLeaderboardResponse,
    ShareRewardsLeaderboardResponse,
    UpvalueLeaderboardEntry,
    UpvalueLeaderboardResponse,
} from 'api/api'
import cn from 'classnames'
import styles from './LeaderboardViewer.module.scss'
import { useLocation } from 'react-router-dom'
import { openCenter, withDepth } from 'components/filters/useStreamFilters'
import { subscribeLeaderboardTicks } from 'state/leaderboardTicks'
import {
    Trophy,
    HandCoins,
    Lightbulb,
    Bot,
    Gem,
    Medal,
    Loader2,
    Rocket,
    Share2,
} from 'lucide-react'
import InitialsAvatar from 'components/users/InitialsAvatar'
import { avatarInitials } from 'components/users/avatarInitials'
import useBsvDisplay from 'components/shared/useBsvDisplay'
import {
    computeLeaderboardRange,
    formatUtcDateLabel,
    isUtcYmd,
    LeaderboardRangeKey,
} from './leaderboardRange'

interface LeaderboardViewerProps {
    modalParams?: { type: string; category?: string; date?: string; [key: string]: unknown }
    closeModal?: () => void
}

type LeaderboardTabKey = 'given' | 'received' | 'boosted' | 'ai_fees' | 'minters' | 'sharers'

const TABS: {
    key: LeaderboardTabKey
    label: string
    icon: React.ElementType
    description: string
    subDescription: string
}[] = [
    {
        key: 'received',
        label: 'Creators',
        icon: Lightbulb,
        description: 'Top Creators',
        subDescription: 'Most upvalued content',
    },
    {
        key: 'given',
        label: 'Patrons',
        icon: HandCoins,
        description: 'Top Patrons',
        subDescription: 'Most upvalue given',
    },
    {
        key: 'boosted',
        label: 'Boosters',
        icon: Rocket,
        description: 'Top Boosters',
        subDescription: 'Most boosts to treechat',
    },
    {
        key: 'minters',
        label: 'Minters',
        icon: Gem,
        description: 'Top Minters',
        subDescription: 'Most ordinals minted',
    },
    {
        key: 'ai_fees',
        label: 'Cyborgs',
        icon: Bot,
        description: 'Top Cyborgs',
        subDescription: 'Most AI used',
    },
    {
        key: 'sharers',
        label: 'Sharers',
        icon: Share2,
        description: 'Top Sharers',
        subDescription: 'Most share rewards earned',
    },
    // {
    //     key: 'posters',
    //     label: 'Speakers',
    //     icon: Megaphone,
    //     description: 'Most public posts created.',
    // },
]

const RANGES: { key: Exclude<LeaderboardRangeKey, 'report'>; label: string }[] = [
    { key: 'today', label: 'Today' },
    { key: '1d', label: '1d' },
    { key: '7d', label: '1w' },
    { key: '1mo', label: '1mo' },
    { key: '1y', label: '1y' },
    { key: 'all', label: 'All' },
]

// Single knob for how many rows the full-screen leaderboard shows.
const LEADERBOARD_LIMIT = 10

export default function LeaderboardViewer({ modalParams, closeModal }: LeaderboardViewerProps) {
    const location = useLocation()
    const initialTab = (modalParams?.category as LeaderboardTabKey) || 'given'
    const reportDate = isUtcYmd(modalParams?.date) ? modalParams.date : undefined
    const [activeTab, setActiveTab] = useState<LeaderboardTabKey>(
        TABS.some(t => t.key === initialTab) ? initialTab : 'given',
    )
    const [activeRange, setActiveRange] = useState<LeaderboardRangeKey>(
        reportDate ? 'report' : 'today',
    )
    const [nowMs, setNowMs] = useState(() => Date.now())
    const { useUsd, formatSatsAmount } = useBsvDisplay()

    const rangeParams = useMemo(
        () => computeLeaderboardRange(activeRange, { reportDate, now: new Date(nowMs) }),
        [activeRange, nowMs, reportDate],
    )
    const rangeOptions = useMemo(
        () =>
            reportDate
                ? ([{ key: 'report', label: formatUtcDateLabel(reportDate) }, ...RANGES] as {
                      key: LeaderboardRangeKey
                      label: string
                  }[])
                : RANGES,
        [reportDate],
    )

    // Keep the "UTC since midnight" badge up-to-date (only needs minute resolution).
    useEffect(() => {
        const id = window.setInterval(() => setNowMs(Date.now()), 60_000)
        return () => window.clearInterval(id)
    }, [])

    const elapsedSinceUtcMidnightLabel = useMemo(() => {
        const start = new Date(nowMs)
        start.setUTCHours(0, 0, 0, 0)
        const elapsedMs = Math.max(0, nowMs - start.getTime())
        const totalMinutes = Math.floor(elapsedMs / 60_000)
        if (totalMinutes < 60) return `${totalMinutes}m`
        const hoursRounded = Math.round(totalMinutes / 60)
        return `${hoursRounded}h`
    }, [nowMs])

    const onSelectUser = (userId: string) => {
        openCenter(withDepth({ user: userId }))
        if (closeModal) closeModal()
    }

    // "today" is real-time (short stale), other ranges are cache-stable (longer stale)
    const staleTime = activeRange === 'today' ? 30_000 : 5 * 60_000

    const query = useQuery<
        | AiFeesLeaderboardResponse
        | UpvalueLeaderboardResponse
        | MintersLeaderboardResponse
        | ShareRewardsLeaderboardResponse
    >({
        queryKey: [
            'leaderboard',
            'modal',
            activeTab,
            activeRange,
            rangeParams.start_date,
            rangeParams.end_date,
        ],
        queryFn: () => {
            if (activeTab === 'ai_fees') {
                return api.getAiFeesLeaderboard({
                    page: 1,
                    per_page: LEADERBOARD_LIMIT,
                    ...rangeParams,
                })
            }
            if (activeTab === 'minters') {
                return api.getMintersLeaderboard({
                    page: 1,
                    per_page: LEADERBOARD_LIMIT,
                    ...rangeParams,
                })
            }
            if (activeTab === 'sharers') {
                return api.getShareRewardsLeaderboard({
                    page: 1,
                    per_page: LEADERBOARD_LIMIT,
                    ...rangeParams,
                })
            }
            return api.getUpvalueLeaderboard({
                category: activeTab,
                page: 1,
                per_page: LEADERBOARD_LIMIT,
                ...rangeParams,
            })
        },
        staleTime,
    })

    // Refetch when the LeaderboardChannel broadcaster signals a board moved
    // (e.g. a share-reward settlement landed) so the open modal stays live.
    const queryClient = useQueryClient()
    useEffect(() => {
        return subscribeLeaderboardTicks(() => {
            queryClient.invalidateQueries({ queryKey: ['leaderboard', 'modal'] })
        })
    }, [queryClient])

    /** Union row type — each tab only accesses the fields relevant to it via runtime guards */
    type LeaderboardRow = {
        user: { id: string; name: string; avatar_url?: string | null }
        total_sats?: number
        upvalue_count?: number
        last_upvalue_at?: string | null
        usage_count?: number
        last_usage_at?: string | null
        mints_count?: number
        last_mint_at?: string | null
        share_count?: number
        last_settled_at?: string | null
    }

    const allRows: LeaderboardRow[] = useMemo(() => {
        const data = query.data
        if (!data) return []
        return ('users' in data ? data.users : []) as LeaderboardRow[]
    }, [query.data])

    const formatCount = useMemo(() => (value: number) => (value || 0).toLocaleString(), [])
    const totalHeaderLabel = useUsd ? 'Total (USD)' : 'Total (sats)'
    const totalDataLabel = useUsd ? 'TOTAL (USD)' : 'TOTAL (SATS)'

    // Update URL when tab changes (avoid history spam)
    useEffect(() => {
        const dateQuery = activeRange === 'report' && reportDate ? `?date=${reportDate}` : ''
        const newUrl = `/modal/leaderboards/${activeTab}${dateQuery}`
        if (location.pathname !== newUrl) {
            window.history.replaceState({}, '', newUrl)
        }
    }, [activeRange, activeTab, location.pathname, reportDate])

    const activeTabDef = useMemo(() => TABS.find(t => t.key === activeTab), [activeTab])

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.titleArea}>
                    <div className={styles.title}>
                        <Trophy size={28} />
                        Leaderboard
                    </div>
                </div>
            </div>

            <div className={styles.tabsContainer}>
                <div className={styles.tabs}>
                    {TABS.map(t => (
                        <button
                            key={t.key}
                            type="button"
                            className={cn(styles.tab, t.key === activeTab && styles.tabActive)}
                            onClick={() => setActiveTab(t.key)}
                        >
                            <t.icon className={styles.tabIcon} size={18} />
                            {t.label}
                        </button>
                    ))}
                </div>

                <div className={styles.rangeTabs}>
                    {rangeOptions.map(r => (
                        <button
                            key={r.key}
                            type="button"
                            className={cn(
                                styles.rangeTab,
                                r.key === activeRange && styles.rangeTabActive,
                            )}
                            onClick={() => setActiveRange(r.key)}
                        >
                            {r.key === 'today' ? elapsedSinceUtcMidnightLabel : r.label}
                        </button>
                    ))}
                </div>

                <div className={styles.subtitle}>
                    <span className={styles.headline}>
                        {activeTabDef?.description || 'Top Contributors'}
                    </span>
                    {activeTabDef?.subDescription && (
                        <span className={styles.description}>{activeTabDef.subDescription}</span>
                    )}
                </div>
            </div>

            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th className={cn(styles.th, styles.rank)}>Rank</th>
                            <th className={styles.th}>User</th>
                            <th className={styles.th}>
                                {activeTab === 'ai_fees'
                                    ? totalHeaderLabel
                                    : activeTab === 'minters'
                                    ? 'Mints'
                                    : totalHeaderLabel}
                            </th>
                            <th className={styles.th}>
                                {activeTab === 'minters'
                                    ? '—'
                                    : activeTab === 'ai_fees'
                                    ? 'Uses'
                                    : activeTab === 'sharers'
                                    ? 'Views'
                                    : 'Count'}
                            </th>
                            <th className={styles.th}>Last Activity</th>
                        </tr>
                    </thead>
                    <tbody>
                        {query.isLoading && (
                            <tr>
                                <td className={cn(styles.td, styles.statusRow)} colSpan={5}>
                                    <Loader2 className={styles.spinner} size={20} />
                                    Loading…
                                </td>
                            </tr>
                        )}
                        {!query.isLoading && query.error && (
                            <tr>
                                <td className={cn(styles.td, styles.statusRow)} colSpan={5}>
                                    Failed to load leaderboard. Please try again later.
                                </td>
                            </tr>
                        )}
                        {!query.isLoading && !query.error && allRows.length === 0 && (
                            <tr>
                                <td className={cn(styles.td, styles.statusRow)} colSpan={5}>
                                    No data available for this selection.
                                </td>
                            </tr>
                        )}
                        {allRows.map((item, idx) => {
                            const rank = idx + 1
                            const rankClass =
                                rank === 1
                                    ? styles.rank1
                                    : rank === 2
                                    ? styles.rank2
                                    : rank === 3
                                    ? styles.rank3
                                    : ''

                            return (
                                <tr key={`${item.user.id}-${idx}`} className={styles.tr}>
                                    <td className={cn(styles.td, styles.rank, rankClass)}>
                                        <div className={styles.rankContent}>
                                            {rank === 1 && (
                                                <Medal size={16} className={styles.medalIcon} />
                                            )}
                                            {rank}
                                        </div>
                                    </td>
                                    <td className={styles.td}>
                                        <div className={styles.userCell}>
                                            {item.user.avatar_url ? (
                                                <img
                                                    className={styles.avatar}
                                                    src={item.user.avatar_url}
                                                    alt={item.user.name}
                                                    onClick={() => onSelectUser(item.user.id)}
                                                />
                                            ) : avatarInitials(item.user.name) ? (
                                                <InitialsAvatar
                                                    className={styles.avatar}
                                                    user={item.user}
                                                    onClick={() => onSelectUser(item.user.id)}
                                                />
                                            ) : (
                                                <div
                                                    className={styles.avatar}
                                                    onClick={() => onSelectUser(item.user.id)}
                                                />
                                            )}
                                            <div
                                                className={styles.userName}
                                                onClick={() => onSelectUser(item.user.id)}
                                            >
                                                {item.user.name}
                                            </div>
                                        </div>
                                    </td>
                                    <td
                                        className={cn(styles.td, styles.mono)}
                                        data-label={
                                            activeTab === 'ai_fees'
                                                ? totalDataLabel
                                                : activeTab === 'minters'
                                                ? 'MINTS'
                                                : totalDataLabel
                                        }
                                    >
                                        {activeTab === 'ai_fees'
                                            ? formatSatsAmount(item.total_sats, {
                                                  compactSats: false,
                                              })
                                            : activeTab === 'minters'
                                            ? formatCount(item.mints_count)
                                            : formatSatsAmount(item.total_sats, {
                                                  compactSats: false,
                                              })}
                                    </td>
                                    <td
                                        className={cn(styles.td, styles.mono)}
                                        data-label={
                                            activeTab === 'minters'
                                                ? '—'
                                                : activeTab === 'ai_fees'
                                                ? 'USES'
                                                : activeTab === 'sharers'
                                                ? 'VIEWS'
                                                : 'COUNT'
                                        }
                                    >
                                        {activeTab === 'minters'
                                            ? '—'
                                            : activeTab === 'ai_fees'
                                            ? formatCount(item.usage_count)
                                            : activeTab === 'sharers'
                                            ? formatCount(item.share_count)
                                            : item.upvalue_count?.toLocaleString?.() ??
                                              item.upvalue_count}
                                    </td>
                                    <td
                                        className={cn(styles.td, styles.mono)}
                                        data-label="LAST ACTIVITY"
                                    >
                                        {(() => {
                                            const lastDate =
                                                activeTab === 'ai_fees'
                                                    ? item.last_usage_at
                                                    : activeTab === 'minters'
                                                    ? item.last_mint_at
                                                    : activeTab === 'sharers'
                                                    ? item.last_settled_at
                                                    : item.last_upvalue_at
                                            return lastDate
                                                ? new Date(lastDate).toLocaleDateString()
                                                : '—'
                                        })()}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    )
}
